"use client";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    q: "Is this replacing my sales team?",
    a: "No. This system handles the lead-to-appointment gap — the part that happens before your closers ever get on a call. Your sales team still closes deals. We just make sure their calendar is filled with qualified, pre-screened homeowners instead of cold leads and no-shows.",
  },
  {
    q: "Will this work if I already have setters?",
    a: "Yes — and it usually makes your setters more effective. The AI handles instant contact and initial qualification so your setters spend time on warmer, more engaged leads instead of chasing cold ones. Some clients reduce setter headcount over time. Others redirect setters to higher-value tasks.",
  },
  {
    q: "How fast can this go live?",
    a: "In most cases the system is live and running ads within 7–14 days of onboarding. There's no long buildout. We've done this before and the setup process is streamlined.",
  },
  {
    q: "Do I need to learn new software?",
    a: "No. Everything is managed for you. You get a CRM view of your pipeline, a calendar with booked appointments, and reporting on what's working. You don't need to manage another platform day-to-day.",
  },
  {
    q: "What kind of solar company is this best for?",
    a: "Founder-led or operator-led companies doing $50k+/month who rely on human follow-up and want to add predictability to their calendar without adding more payroll. If you have motion and want consistency, this is built for you.",
  },
  {
    q: "What if we already have leads coming in?",
    a: "Even better. If you have lead volume and your follow-up is inconsistent or slow, that's the exact leak we fix. The AI layer can be added on top of your existing lead sources — not just new ad campaigns.",
  },
  {
    q: "How is this different from another lead gen agency?",
    a: "Most agencies deliver leads and disappear. We stay in the process all the way to booked appointment. Ads, follow-up, qualification, and booking are all connected in one system. We're accountable to booked appointments — not lead volume. That's a fundamentally different model.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-20 px-6 md:px-10 bg-[#F8F8F4]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-12">
          <p className="text-xs font-bold tracking-widest uppercase text-amber-500 mb-3">Common Questions</p>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
            FAQ
          </h2>
        </div>

        {/* FAQ list */}
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={faq.q}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                <ChevronDown
                  size={18}
                  className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? "rotate-180" : ""}`}
                />
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5 border-t border-gray-100">
                  <p className="text-sm text-gray-500 leading-relaxed pt-4">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
